/** Per-pane rendering pipeline (ARCHITECTURE §4–5): steps 1–10, plus magnification (D-021) and starbursts (D-018). */

import type { Lang } from '../i18n';
import { getKernel, kernelKey, type KernelPart } from '../optics/kernels';
import {
  AMBLYOPIA_CONTRAST, MAX_SPOTS, amblyopiaBlurArcmin, drFloaters, retinaSpots, type RetinaParams,
} from '../optics/retina';
import { clamp, type PaneConfig } from '../optics/types';
import { RENDER_H, RENDER_W, type Scene, type SceneLight, type ToneCurve } from '../scenes/types';
import {
  bindTarget, bindTextureUnit, compileProgram, createFloatTexture, createTarget, createUploadTexture,
  deleteTarget, detectCaps, drawFullscreen, getContext, uploadCanvas, type GLCaps, type Target,
} from './gl';
import {
  FS_BLIT, FS_CONVOLVE, FS_FINAL, FS_GAUSS, FS_LIGHTS, FS_LINEARIZE, FS_RETINA, FS_SCATTER, FS_STREAKS, VS_FULLSCREEN,
} from './shaders';

const LEVELS = [1, 2, 4, 8, 16, 32, 64];
const KERNEL_TEX_LIMIT = 48;
const MAX_LIGHTS = 32;
const MAX_STREAK_LIGHTS = 24;
const MAX_SPIKES = 16;
/** Lights dimmer than this (× diffuse white) get no streaks. */
const STREAK_MIN_INTENSITY = 40;

/** Scatter point-spread mix, degrees and weights (OPTICS §8). */
const SCATTER_SCALES: ReadonlyArray<readonly [number, number]> = [[0.4, 0.25], [1.2, 0.3], [3.5, 0.25], [10, 0.2]];

/** Retinal blur ladder in degrees, so retinal defects look the same in every scene (D-024). */
const RETINA_BLURS_DEG = [0.07, 0.21, 0.56, 1.67];

interface LayerRes {
  id: string;
  V: number;
  /** levels[i] holds the layer at L = LEVELS[i]. */
  levels: Target[];
}

interface KernelEntry {
  parts: KernelPart[];
  tex: WebGLTexture[];
}

/** A scene light in render pixels (y-up), for the streak pass. */
interface LightPx {
  layer: string;
  x: number;
  y: number;
  rx: number;
  ry: number;
  color: [number, number, number];
  intensity: number;
}

type U = WebGLUniformLocation | null;

interface Programs {
  linearize: { prog: WebGLProgram; uScale: U };
  blit: { prog: WebGLProgram; uScale: U; uZoom: U };
  lights: { prog: WebGLProgram; uCount: U; uLights: U; uColors: U };
  convolve: { prog: WebGLProgram; uR: U; uSize: U };
  gauss: { prog: WebGLProgram; uStep: U; uSigma: U; uRadius: U };
  scatter: { prog: WebGLProgram; uF: U; uTrans: U };
  streaks: {
    prog: WebGLProgram; uCount: U; uGeo: U; uCol: U; uSpikes: U; uSpikeCount: U; uEnergy: U; uWidth: U; uTrans: U;
  };
  retina: {
    prog: WebGLProgram; uFix: U; uSize: U; uPpd: U; uStages: U; uHr: U; uAmblyopia: U; uAmbContrast: U; uDark: U;
    uSpots: U; uSpotCount: U; uFloaters: U; uFloaterCount: U;
  };
  final: { prog: WebGLProgram; uSplit: U; uExposure: U; uKnee: U; uK: U; uWhite: U };
}

export interface Timings {
  scene: number;
  pane: [number, number];
  present: number;
}

export type Side = 0 | 1;

/** Deterministic spike set for the streak pass: [cos, sin, length factor, weight] × count. */
function spikePattern(pattern: number): { data: Float32Array; count: number } {
  const count = pattern === 1 ? 12 : 8;
  let seed = pattern * 7919 + 17;
  const rnd = (): number => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  const data = new Float32Array(MAX_SPIKES * 4);
  const lengths: number[] = [];
  let total = 0;
  for (let k = 0; k < count; k++) {
    const len = pattern === 1 ? 0.45 + 0.55 * rnd() : 0.3 + 0.5 * rnd();
    lengths.push(len);
    total += len;
  }
  for (let k = 0; k < count; k++) {
    const a = ((k + 0.5) / count) * Math.PI * 2 + (rnd() - 0.5) * (Math.PI / count);
    const len = lengths[k]!;
    data.set([Math.cos(a), Math.sin(a), len, len / total], k * 4);
  }
  return { data, count };
}

export class Pipeline {
  readonly gl: WebGL2RenderingContext;
  readonly caps: GLCaps;
  readonly timings: Timings = { scene: 0, pane: [0, 0], present: 0 };
  /** When true, wait for the GPU after each pane so timings are real. Costs throughput. */
  measureGpu = false;

  private readonly canvas: HTMLCanvasElement;
  private progs!: Programs;
  private uploadTex!: WebGLTexture;
  private layerCanvases: HTMLCanvasElement[] = [];
  private layerPool: LayerRes[] = [];
  private layers: LayerRes[] = [];
  private tmp: Target[] = [];
  private tmp2: Target[] = [];
  private panePyr: (Target | null)[] = [];
  private acc!: Target;
  private panes!: [Target, Target];
  private paneOut!: [Target, Target];
  private paneRet!: [Target, Target];
  private retBlur: [(Target | null)[], (Target | null)[]] = [[], []];
  private ambBlur: [Target | null, Target | null] = [null, null];
  private retBlurKeys: [string, string] = ['', ''];
  private retinaKeys: [string, string] = ['', ''];
  private paneKeys: [string, string] = ['', ''];
  private sceneKey = '';
  private ppd = 0;
  private lightsPx: LightPx[] = [];
  private kernels = new Map<string, KernelEntry>();
  private spikeCache = new Map<number, { data: Float32Array; count: number }>();

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const gl = getContext(canvas);
    if (!gl) throw new Error('WebGL2 unavailable');
    this.gl = gl;
    this.caps = detectCaps(gl);
    this.allocate();
  }

  private allocate(): void {
    const gl = this.gl;
    const u = (p: WebGLProgram, n: string): U => gl.getUniformLocation(p, n);
    const make = (fs: string, name: string, samplers: string[]): WebGLProgram => {
      const p = compileProgram(gl, VS_FULLSCREEN, fs, name);
      gl.useProgram(p);
      samplers.forEach((s, i) => gl.uniform1i(u(p, s), i));
      return p;
    };
    const linearize = make(FS_LINEARIZE, 'linearize', ['u_src']);
    const blit = make(FS_BLIT, 'blit', ['u_src']);
    const lights = make(FS_LIGHTS, 'lights', []);
    const convolve = make(FS_CONVOLVE, 'convolve', ['u_src', 'u_kernel']);
    const gauss = make(FS_GAUSS, 'gauss', ['u_src']);
    const scatter = make(FS_SCATTER, 'scatter', ['u_img', 'u_scatter']);
    const streaks = make(FS_STREAKS, 'streaks', []);
    const retina = make(FS_RETINA, 'retina', ['u_img', 'u_b0', 'u_b1', 'u_b2', 'u_b3', 'u_bAmb']);
    const final = make(FS_FINAL, 'final', ['u_left', 'u_right']);
    this.progs = {
      linearize: { prog: linearize, uScale: u(linearize, 'u_scale') },
      blit: { prog: blit, uScale: u(blit, 'u_scale'), uZoom: u(blit, 'u_zoom') },
      lights: { prog: lights, uCount: u(lights, 'u_count'), uLights: u(lights, 'u_lights'), uColors: u(lights, 'u_colors') },
      convolve: { prog: convolve, uR: u(convolve, 'u_R'), uSize: u(convolve, 'u_size') },
      gauss: { prog: gauss, uStep: u(gauss, 'u_step'), uSigma: u(gauss, 'u_sigma'), uRadius: u(gauss, 'u_radius') },
      scatter: { prog: scatter, uF: u(scatter, 'u_f'), uTrans: u(scatter, 'u_trans') },
      streaks: {
        prog: streaks, uCount: u(streaks, 'u_count'), uGeo: u(streaks, 'u_geo'), uCol: u(streaks, 'u_col'),
        uSpikes: u(streaks, 'u_spikes'), uSpikeCount: u(streaks, 'u_spikeCount'), uEnergy: u(streaks, 'u_energy'),
        uWidth: u(streaks, 'u_width'), uTrans: u(streaks, 'u_trans'),
      },
      retina: {
        prog: retina, uFix: u(retina, 'u_fix'), uSize: u(retina, 'u_size'), uPpd: u(retina, 'u_ppd'),
        uStages: u(retina, 'u_stages'), uHr: u(retina, 'u_hr'), uAmblyopia: u(retina, 'u_amblyopia'),
        uAmbContrast: u(retina, 'u_ambContrast'), uDark: u(retina, 'u_dark'),
        uSpots: u(retina, 'u_spots'), uSpotCount: u(retina, 'u_spotCount'),
        uFloaters: u(retina, 'u_floaters'), uFloaterCount: u(retina, 'u_floaterCount'),
      },
      final: {
        prog: final, uSplit: u(final, 'u_split'), uExposure: u(final, 'u_exposure'),
        uKnee: u(final, 'u_knee'), uK: u(final, 'u_k'), uWhite: u(final, 'u_white'),
      },
    };
    this.uploadTex = createUploadTexture(gl);
    const level = (L: number): Target => createTarget(gl, this.caps, RENDER_W / L, RENDER_H / L, gl.LINEAR);
    this.tmp = LEVELS.map(level);
    this.tmp2 = LEVELS.map(level);
    this.panePyr = LEVELS.map((L) => (L === 1 ? null : level(L)));
    this.acc = level(1);
    this.panes = [level(1), level(1)];
    this.paneOut = [level(1), level(1)];
    this.paneRet = [level(1), level(1)];
    this.retBlur = [RETINA_BLURS_DEG.map(() => null), RETINA_BLURS_DEG.map(() => null)];
  }

  private ensureLayers(count: number): void {
    const gl = this.gl;
    while (this.layerPool.length < count) {
      this.layerPool.push({
        id: '',
        V: 0,
        levels: LEVELS.map((L) => createTarget(gl, this.caps, RENDER_W / L, RENDER_H / L, gl.LINEAR)),
      });
      const c = document.createElement('canvas');
      c.width = RENDER_W;
      c.height = RENDER_H;
      this.layerCanvases.push(c);
    }
    this.layers = this.layerPool.slice(0, count);
  }

  get pixelsPerDegree(): number {
    return this.ppd;
  }

  private blit(src: WebGLTexture, dst: Target | null, scale = 1, zoom = 1): void {
    const gl = this.gl;
    bindTarget(gl, dst);
    gl.useProgram(this.progs.blit.prog);
    gl.uniform1f(this.progs.blit.uScale, scale);
    gl.uniform1f(this.progs.blit.uZoom, zoom);
    bindTextureUnit(gl, 0, src);
    drawFullscreen(gl);
  }

  private drawLights(target: Target, lights: SceneLight[]): void {
    const gl = this.gl;
    const n = Math.min(lights.length, MAX_LIGHTS);
    const geo = new Float32Array(n * 4);
    const col = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      const l = lights[i]!;
      geo[i * 4] = RENDER_W / 2 + l.x * this.ppd;
      geo[i * 4 + 1] = RENDER_H / 2 + l.y * this.ppd;
      geo[i * 4 + 2] = Math.max(0.75, l.rx * this.ppd);
      geo[i * 4 + 3] = Math.max(0.75, l.ry * this.ppd);
      col[i * 3] = l.color[0] * l.intensity;
      col[i * 3 + 1] = l.color[1] * l.intensity;
      col[i * 3 + 2] = l.color[2] * l.intensity;
    }
    bindTarget(gl, target);
    gl.enable(gl.BLEND);
    gl.blendFuncSeparate(gl.ONE, gl.ONE, gl.ZERO, gl.ONE);
    gl.useProgram(this.progs.lights.prog);
    gl.uniform1i(this.progs.lights.uCount, n);
    gl.uniform4fv(this.progs.lights.uLights, geo);
    gl.uniform3fv(this.progs.lights.uColors, col);
    drawFullscreen(gl);
    gl.disable(gl.BLEND);
  }

  /** Draw, upload, light and pyramid the scene layers. Returns true when the scene changed. */
  setScene(scene: Scene, lang: Lang): boolean {
    const key = `${scene.id}|${lang}`;
    if (key === this.sceneKey) return false;
    const t0 = performance.now();
    const gl = this.gl;
    this.ppd = RENDER_W / scene.hfov;
    this.ensureLayers(scene.layers.length);
    const lights = scene.lights(lang);
    this.lightsPx = lights.map((l) => ({
      layer: l.layer,
      x: RENDER_W / 2 + l.x * this.ppd,
      y: RENDER_H / 2 + l.y * this.ppd,
      rx: Math.max(0.75, l.rx * this.ppd),
      ry: Math.max(0.75, l.ry * this.ppd),
      color: l.color,
      intensity: l.intensity,
    }));
    gl.disable(gl.BLEND);
    scene.layers.forEach((layer, i) => {
      const canvas = this.layerCanvases[i]!;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('2d context unavailable');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, RENDER_W, RENDER_H);
      ctx.save();
      layer.draw(ctx, lang);
      ctx.restore();

      const res = this.layers[i]!;
      res.id = layer.id;
      res.V = layer.V;
      uploadCanvas(gl, this.uploadTex, canvas);
      bindTarget(gl, res.levels[0]!);
      gl.useProgram(this.progs.linearize.prog);
      gl.uniform1f(this.progs.linearize.uScale, 1);
      bindTextureUnit(gl, 0, this.uploadTex);
      drawFullscreen(gl);

      if (layer.emissive) {
        // Shaped HDR emitters (neon text, screens): drawn on the same canvas, added on top.
        ctx.clearRect(0, 0, RENDER_W, RENDER_H);
        ctx.save();
        layer.emissive(ctx, lang);
        ctx.restore();
        uploadCanvas(gl, this.uploadTex, canvas);
        bindTarget(gl, res.levels[0]!);
        gl.enable(gl.BLEND);
        gl.blendFuncSeparate(gl.ONE, gl.ONE, gl.ZERO, gl.ONE);
        gl.useProgram(this.progs.linearize.prog);
        gl.uniform1f(this.progs.linearize.uScale, layer.emissiveScale ?? 1);
        bindTextureUnit(gl, 0, this.uploadTex);
        drawFullscreen(gl);
        gl.disable(gl.BLEND);
      }

      const layerLights = lights.filter((l) => l.layer === layer.id);
      if (layerLights.length) this.drawLights(res.levels[0]!, layerLights);

      for (let k = 1; k < LEVELS.length; k++) this.blit(res.levels[k - 1]!.tex, res.levels[k]!);
    });
    this.sceneKey = key;
    this.paneKeys = ['', ''];
    this.retBlurKeys = ['', ''];
    this.retinaKeys = ['', ''];
    this.timings.scene = performance.now() - t0;
    return true;
  }

  private paneKey(cfg: PaneConfig): string {
    return `${cfg.key}|${this.sceneKey}`;
  }

  paneNeedsRender(side: Side, cfg: PaneConfig): boolean {
    return this.paneKeys[side] !== this.paneKey(cfg);
  }

  private kernelEntry(cfg: PaneConfig, V: number): KernelEntry {
    const key = kernelKey(cfg, V, this.ppd);
    const hit = this.kernels.get(key);
    if (hit) return hit;
    const parts = getKernel(cfg, V, this.ppd);
    const tex = parts.map((p) => createFloatTexture(this.gl, p.size, p.data));
    if (this.kernels.size >= KERNEL_TEX_LIMIT) {
      const oldest = this.kernels.keys().next().value;
      if (oldest !== undefined) {
        for (const t of this.kernels.get(oldest)!.tex) this.gl.deleteTexture(t);
        this.kernels.delete(oldest);
      }
    }
    const entry = { parts, tex };
    this.kernels.set(key, entry);
    return entry;
  }

  /** Render one pane through steps 5–8 (plus magnification and streaks). Returns true when work was done. */
  renderPane(side: Side, cfg: PaneConfig): boolean {
    const key = this.paneKey(cfg);
    if (this.paneKeys[side] === key) return false;
    const t0 = performance.now();
    const gl = this.gl;
    const pane = this.panes[side];

    gl.disable(gl.BLEND);
    bindTarget(gl, pane);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    for (const layer of this.layers) {
      const { parts, tex } = this.kernelEntry(cfg, layer.V);

      bindTarget(gl, this.acc);
      gl.clear(gl.COLOR_BUFFER_BIT);

      parts.forEach((part, i) => {
        const li = LEVELS.indexOf(part.L);
        const src = layer.levels[li]!;
        const dst = this.tmp[li]!;

        gl.disable(gl.BLEND);
        bindTarget(gl, dst);
        gl.useProgram(this.progs.convolve.prog);
        bindTextureUnit(gl, 0, src.tex);
        bindTextureUnit(gl, 1, tex[i]!);
        gl.uniform1i(this.progs.convolve.uR, part.R);
        gl.uniform2i(this.progs.convolve.uSize, dst.w, dst.h);
        drawFullscreen(gl);

        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE);
        this.blit(dst.tex, this.acc);
      });

      // Streaks belong to the layer's point-spread, so nearer layers occlude them like the lights themselves.
      this.streakPass(layer.id, cfg);

      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      this.blit(this.acc.tex, pane);
    }
    gl.disable(gl.BLEND);

    // Spectacle magnification (aphakia): the retinal image is scaled before scatter and the retina (D-021).
    if (cfg.magnification !== 1) {
      this.blit(pane.tex, this.acc, 1, cfg.magnification);
      this.blit(this.acc.tex, pane);
    }

    this.scatterPass(side, cfg);

    if (this.measureGpu) gl.finish();
    this.paneKeys[side] = key;
    this.timings.pane[side] = performance.now() - t0;
    return true;
  }

  /** Build the pane pyramid of a full-resolution source into panePyr[1..]. */
  private buildPyramid(src: Target): void {
    this.gl.disable(this.gl.BLEND);
    let prev: Target = src;
    for (let li = 1; li < LEVELS.length; li++) {
      const dst = this.panePyr[li]!;
      this.blit(prev.tex, dst);
      prev = dst;
    }
  }

  /** Separable Gaussian (σ in full-res px) at a pyramid level with σ_L ≈ 2.5 px; the pyramid of `src` must be built. */
  private gaussian(src: Target, sigmaPx: number, out: (L: number) => Target, maxRadius = 32): Target {
    const gl = this.gl;
    const li = clamp(Math.round(Math.log2(Math.max(sigmaPx, 1) / 2.5)), 0, LEVELS.length - 1);
    const L = LEVELS[li]!;
    const sigmaL = Math.max(0.5, sigmaPx / L);
    const source = li === 0 ? src : this.panePyr[li]!;
    const a = this.tmp[li]!;
    const dst = out(L);
    gl.disable(gl.BLEND);
    gl.useProgram(this.progs.gauss.prog);
    gl.uniform1f(this.progs.gauss.uSigma, sigmaL);
    gl.uniform1i(this.progs.gauss.uRadius, Math.min(maxRadius, Math.ceil(3 * sigmaL)));
    bindTarget(gl, a);
    bindTextureUnit(gl, 0, source.tex);
    gl.uniform2f(this.progs.gauss.uStep, 1 / a.w, 0);
    drawFullscreen(gl);
    bindTarget(gl, dst);
    bindTextureUnit(gl, 0, a.tex);
    gl.uniform2f(this.progs.gauss.uStep, 0, 1 / a.h);
    drawFullscreen(gl);
    return dst;
  }

  /** A persistent target at level L, reusing `slot` when it already has that size. */
  private levelTarget(slot: Target | null, L: number): Target {
    if (slot && slot.w === RENDER_W / L) return slot;
    if (slot) deleteTarget(this.gl, slot);
    return createTarget(this.gl, this.caps, RENDER_W / L, RENDER_H / L, this.gl.LINEAR);
  }

  /** Step 8: scatter mix and transmission from the composited pane into paneOut. */
  private scatterPass(side: Side, cfg: PaneConfig): void {
    const gl = this.gl;
    const pane = this.panes[side];
    this.buildPyramid(pane);
    bindTarget(gl, this.acc);
    gl.clear(gl.COLOR_BUFFER_BIT);
    for (const [sigmaDeg, weight] of SCATTER_SCALES) {
      const blurred = this.gaussian(pane, sigmaDeg * this.ppd, (L) => this.tmp2[LEVELS.indexOf(L)]!);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE);
      this.blit(blurred.tex, this.acc, weight);
    }
    gl.disable(gl.BLEND);
    bindTarget(gl, this.paneOut[side]);
    gl.useProgram(this.progs.scatter.prog);
    bindTextureUnit(gl, 0, pane.tex);
    bindTextureUnit(gl, 1, this.acc.tex);
    gl.uniform1f(this.progs.scatter.uF, cfg.scatter);
    gl.uniform3f(this.progs.scatter.uTrans, cfg.transmission[0], cfg.transmission[1], cfg.transmission[2]);
    drawFullscreen(gl);
  }

  /**
   * Starburst streaks around the bright lights of one layer, added to the layer accumulator (D-018).
   * Magnification, scatter and transmission are applied later with the rest of the pane.
   */
  private streakPass(layerId: string, cfg: PaneConfig): void {
    if (cfg.streaks <= 0 || this.lightsPx.length === 0) return;
    const bright = this.lightsPx
      .filter((l) => l.layer === layerId && l.intensity >= STREAK_MIN_INTENSITY)
      .slice(0, MAX_STREAK_LIGHTS);
    if (bright.length === 0) return;
    const gl = this.gl;
    const geo = new Float32Array(MAX_STREAK_LIGHTS * 4);
    const col = new Float32Array(MAX_STREAK_LIGHTS * 3);
    bright.forEach((l, i) => {
      const flux = l.intensity * Math.PI * l.rx * l.ry;
      const lengthDeg = Math.min(2.5, 1.2 * Math.pow(l.intensity / 100, 0.25));
      geo.set([l.x, l.y, flux, lengthDeg * this.ppd], i * 4);
      col.set(l.color, i * 3);
    });
    let spikes = this.spikeCache.get(cfg.streakPattern);
    if (!spikes) {
      spikes = spikePattern(cfg.streakPattern);
      this.spikeCache.set(cfg.streakPattern, spikes);
    }
    const s = this.progs.streaks;
    bindTarget(gl, this.acc);
    gl.enable(gl.BLEND);
    gl.blendFuncSeparate(gl.ONE, gl.ONE, gl.ZERO, gl.ONE);
    gl.useProgram(s.prog);
    gl.uniform1i(s.uCount, bright.length);
    gl.uniform4fv(s.uGeo, geo);
    gl.uniform3fv(s.uCol, col);
    gl.uniform4fv(s.uSpikes, spikes.data);
    gl.uniform1i(s.uSpikeCount, spikes.count);
    gl.uniform1f(s.uEnergy, cfg.streaks);
    gl.uniform1f(s.uWidth, 0.8);
    gl.uniform3f(s.uTrans, 1, 1, 1);
    drawFullscreen(gl);
    gl.disable(gl.BLEND);
  }

  /** Blurs of paneOut for the retinal pass (degree ladder + amblyopia); rebuilt only when the pane or the degree changed. */
  private ensureRetinaBlurs(side: Side, amblyopia: RetinaParams['amblyopia']): void {
    const key = `${this.paneKeys[side]}|${amblyopia}`;
    if (this.retBlurKeys[side] === key) return;
    const src = this.paneOut[side];
    this.buildPyramid(src);
    const slots = this.retBlur[side];
    RETINA_BLURS_DEG.forEach((deg, k) => {
      slots[k] = this.gaussian(src, deg * this.ppd, (L) => this.levelTarget(slots[k] ?? null, L));
    });
    if (amblyopia > 0) {
      const sigmaPx = (amblyopiaBlurArcmin(amblyopia) / 60) * this.ppd;
      this.ambBlur[side] = this.gaussian(src, sigmaPx, (L) => this.levelTarget(this.ambBlur[side], L));
    }
    this.retBlurKeys[side] = key;
  }

  /** Step 9: retinal pass around the fixation point. Returns true when work was done. */
  applyRetina(side: Side, params: RetinaParams): boolean {
    const key = [
      this.paneKeys[side], params.glaucoma, params.amd, params.dr, params.hr, params.rp, params.amblyopia,
      params.dark.toFixed(4), params.fixation[0].toFixed(1), params.fixation[1].toFixed(1),
    ].join('|');
    if (this.retinaKeys[side] === key) return false;
    this.ensureRetinaBlurs(side, params.amblyopia);
    const gl = this.gl;
    const spots = retinaSpots(params);
    const floaters = drFloaters(params.dr);
    const spotData = new Float32Array(MAX_SPOTS * 4);
    spots.forEach((s, i) => spotData.set([s.dx, s.dy, s.r, s.strength], i * 4));
    const floaterData = new Float32Array(4 * 4);
    floaters.forEach((f, i) => floaterData.set([f.dx, f.dy, f.rx, f.ry], i * 4));

    gl.disable(gl.BLEND);
    bindTarget(gl, this.paneRet[side]);
    const r = this.progs.retina;
    gl.useProgram(r.prog);
    const out = this.paneOut[side];
    bindTextureUnit(gl, 0, out.tex);
    this.retBlur[side].forEach((t, k) => bindTextureUnit(gl, 1 + k, (t ?? out).tex));
    bindTextureUnit(gl, 5, (params.amblyopia > 0 ? this.ambBlur[side] ?? out : out).tex);
    gl.uniform2f(r.uFix, params.fixation[0], params.fixation[1]);
    gl.uniform2f(r.uSize, RENDER_W, RENDER_H);
    gl.uniform1f(r.uPpd, this.ppd);
    gl.uniform4i(r.uStages, params.glaucoma, params.amd, params.dr, params.rp);
    gl.uniform1i(r.uHr, params.hr);
    gl.uniform1i(r.uAmblyopia, params.amblyopia);
    gl.uniform1f(r.uAmbContrast, AMBLYOPIA_CONTRAST[params.amblyopia] ?? 0);
    gl.uniform1f(r.uDark, params.dark);
    gl.uniform4fv(r.uSpots, spotData);
    gl.uniform1i(r.uSpotCount, spots.length);
    gl.uniform4fv(r.uFloaters, floaterData);
    gl.uniform1i(r.uFloaterCount, floaters.length);
    drawFullscreen(gl);
    this.retinaKeys[side] = key;
    return true;
  }

  /** Step 10: pick a pane by split x, exposure, tone map, sRGB → screen. */
  present(split: number, exposure: number, useRetina = false, tone?: ToneCurve): void {
    const t0 = performance.now();
    const gl = this.gl;
    gl.disable(gl.BLEND);
    bindTarget(gl, null, this.canvas.width, this.canvas.height);
    const f = this.progs.final;
    gl.useProgram(f.prog);
    const src = useRetina ? this.paneRet : this.paneOut;
    bindTextureUnit(gl, 0, src[0].tex);
    bindTextureUnit(gl, 1, src[1].tex);
    gl.uniform1f(f.uSplit, split);
    gl.uniform1f(f.uExposure, exposure);
    gl.uniform1f(f.uKnee, tone ? tone.knee : 0);
    gl.uniform1f(f.uK, tone ? tone.k : 1);
    gl.uniform1f(f.uWhite, tone ? tone.white : 1);
    drawFullscreen(gl);
    this.timings.present = performance.now() - t0;
  }

  /** Forget cached panes and kernel textures (after a MAXR change or a scene rebuild). */
  invalidate(): void {
    for (const e of this.kernels.values()) e.tex.forEach((t) => this.gl.deleteTexture(t));
    this.kernels.clear();
    this.paneKeys = ['', ''];
    this.retBlurKeys = ['', ''];
    this.retinaKeys = ['', ''];
  }

  /** Kernel parts for a config and layer (debug / tests). */
  debugKernel(cfg: PaneConfig, V: number): KernelPart[] {
    return this.kernelEntry(cfg, V).parts;
  }

  dispose(): void {
    const gl = this.gl;
    for (const e of this.kernels.values()) e.tex.forEach((t) => gl.deleteTexture(t));
    this.kernels.clear();
    this.tmp.forEach((t) => deleteTarget(gl, t));
    this.tmp2.forEach((t) => deleteTarget(gl, t));
    this.panePyr.forEach((t) => t && deleteTarget(gl, t));
    this.layerPool.forEach((l) => l.levels.forEach((t) => deleteTarget(gl, t)));
    deleteTarget(gl, this.acc);
    this.panes.forEach((t) => deleteTarget(gl, t));
    this.paneOut.forEach((t) => deleteTarget(gl, t));
    this.paneRet.forEach((t) => deleteTarget(gl, t));
    this.retBlur.forEach((set) => set.forEach((t) => t && deleteTarget(gl, t)));
    this.ambBlur.forEach((t) => t && deleteTarget(gl, t));
    gl.deleteTexture(this.uploadTex);
  }
}
